import React from 'react';
import { AudioManager } from './audioManager';
import { ChatMsg } from "../../pages/sharedTypes";
import { UIMsg } from "../../pages/sharedTypes";
import { flushSync } from 'react-dom';
import { dialogControlConfig, getRealtimeVoiceServerUrl } from './config';
import { getJWTToken, verifyJWTToken, refreshJWTToken } from '../auth';
import {
  VoiceEngineOptions,
  StartSessionOptions,
  RealtimeVoiceEngineHandle,
  VoiceProvider,
  WordBoundary
} from './types';
import { devLog } from '../../utils/logger';
import { AudioSession } from 'capacitor-audio-session';

const SERVER_URL = getRealtimeVoiceServerUrl();

/**
 * Creates a realtime voice engine instance
 * Supports multiple providers: Huoshan (Chinese optimized) and Azure VoiceLive (multilingual)
 * 
 * @param onUserTextUpdate - Callback for real-time user speech recognition updates
 * @param setChatMessages - Optional React state setter for chat messages
 * @param setUIMessages - Optional React state setter for UI messages
 * @param onClearLastText - Optional callback to clear displayed text
 * @param onAiTextUpdate - Optional callback for real-time AI reply text updates
 * @returns RealtimeVoiceEngineHandle - Control interface for the voice engine
 */
export function createRealtimeVoiceEngine(
  onUserTextUpdate: (text: string) => void,
  setChatMessages?: React.Dispatch<React.SetStateAction<ChatMsg[]>>,
  setUIMessages?: React.Dispatch<React.SetStateAction<UIMsg[]>>,
  onClearLastText?: () => void,
  onAiTextUpdate?: (text: string) => void
): RealtimeVoiceEngineHandle {

  const socketRef = { current: null as WebSocket | null };
  const audioManagerRef = { current: null as AudioManager | null };
  const userTextRef = { current: "" };
  const aiTextRef = { current: "" };
  const shouldPlayRef = { current: true };  // 初始为 true，允许播放 AI 音频
  const needsReconnectRef = { current: false };
  const connectPromiseRef = { current: null as Promise<void> | null };
  const providerRef = { current: 'huoshan' as VoiceProvider };
  const languageRef = { current: 'zh' };
  const pushMessagesTimerRef = { current: null as number | null };
  const shouldStartTimerRef = { current: false };
  const inactivityTimerRef = { current: null as ReturnType<typeof setTimeout> | null };
  const noResponseTimeoutRef = { current: null as ReturnType<typeof setTimeout> | null };
  let onSpeechStartCallback: (() => void) | null = null;
  let onSpeechEndCallback: (() => void) | null = null;
  let onSessionReadyCallback: ((id?: string) => void) | null = null;
  let onErrorCallback: ((message: string) => void) | null = null;
  let onQuotaExceededCallback: ((message: string) => void) | null = null;
  let onRateLimitCallback: ((retryAfter: number) => void) | null = null;
  let onAISpeakingStateChangeCallback: ((isSpeaking: boolean) => void) | null = null;
  let onSuggestionsCallback: ((suggestions: string[]) => void) | null = null;
  let onWordBoundaryCallback: ((data: WordBoundary) => void) | null = null;
  let onConnectionLostCallback: ((retryCount: number, waitTime: number) => void) | null = null;
  let onVisibilityResumeCallback: (() => void) | null = null;
  const speakerRef = { current: null as string | null };
  const systemRoleRef = { current: null as string | null };
  const personaIdRef = { current: null as string | null };
  const genderRef = { current: null as string | null };
  const userSpeakingTimestampRef = { current: 0 }; // 记录 user_speaking 事件时间，用于过滤误触发
  const stopPromiseRef = { current: null as Promise<void> | null }; // 防止 stop 和 start 竞态
  const intentRef = { current: 'new' as 'new' | 'resume' }; // Default to 'new' (Full Greeting)
  const textOnlyModeRef = { current: false };
  const userNameRef = { current: null as string | null };
  const conversationIdRef = { current: null as string | null };
  const isWaitingForPlaybackToEndRef = { current: false }; // 标记是否收到 chat_ended 并等待音频播完
  const isManualStopRef = { current: false }; // 记录是否为人为主动停止
  const reconnectAttemptsRef = { current: 0 };
  const maxReconnectAttempts = 4;
  const backoffDelays = [1000, 2000, 4000, 8000];
  const reconnectTimerRef = { current: null as any };
  const endSessionSentRef = { current: false };
  const summaryDebugByConversationRef = { current: {} as Record<string, any> };
  const summaryResultWaitersRef = { current: {} as Record<string, Array<(result: any | null) => void>> };
  const audioManagerCleanupTimerRef = { current: null as ReturnType<typeof setTimeout> | null };
  const visibilityHandlerRef = { current: null as (() => void) | null };
  const wasActiveBeforeHiddenRef = { current: false };

  const isTerminalSummaryResult = (result: any): boolean => {
    const status = result?.status;
    return status === 'summarized' || status === 'skipped' || status === 'error';
  };

  const { inactivityPromptMs, noResponseAutoStopMs } = dialogControlConfig;

  const notifyAutoStop = () => {
    if (typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('dialog-auto-stop'));
  };

  const clearNoResponseTimeout = () => {
    if (noResponseTimeoutRef.current) {
      clearTimeout(noResponseTimeoutRef.current);
      noResponseTimeoutRef.current = null;
    }
  };

  const startNoResponseTimeout = () => {
    if (providerRef.current !== 'huoshan' || !shouldStartTimerRef.current) {
      return;
    }
    clearNoResponseTimeout();
    noResponseTimeoutRef.current = setTimeout(() => {
      stop()
        .catch((err) => console.error('[VoiceEngine] Failed to stop after inactivity', err))
        .finally(() => notifyAutoStop());
    }, noResponseAutoStopMs);
  };

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const resetInactivityTimer = (interval: number = inactivityPromptMs) => {
    if (
      providerRef.current !== 'huoshan' ||
      !shouldStartTimerRef.current ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN
    ) {
      clearInactivityTimer();
      return;
    }

    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      inactivityTimerRef.current = null;
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN ||
        providerRef.current !== 'huoshan'
      ) {
        return;
      }

      try {
        shouldPlayRef.current = true;
        socketRef.current.send(
          JSON.stringify({
            command: "trigger_chat_text_query",
            content: "系统消息：用模型的既定的语言，画风和音色询问用户是否需要继续聊天",
            source: "system",
          })
        );
        startNoResponseTimeout();
      } catch (error) {
        console.error('[VoiceEngine] Failed to send inactivity prompt', error);
      }
    }, interval);
  };

  const sendConnectPayload = () => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    const payload: Record<string, unknown> = {
      command: "connect",
      provider: providerRef.current,
      language: languageRef.current,
      conversation_id: conversationIdRef.current
    };
    if (speakerRef.current) {
      payload.speaker = speakerRef.current;
    }
    if (systemRoleRef.current) {
      payload.system_role = systemRoleRef.current;
    }
    if (personaIdRef.current) {
      payload.persona_id = personaIdRef.current;
    }
    if (genderRef.current) {
      payload.gender = genderRef.current;
    }
    if (userNameRef.current) {
      payload.user_name = userNameRef.current;
    }
    if (intentRef.current) {
      payload.intent = intentRef.current;
    }
    socketRef.current.send(JSON.stringify(payload));
    needsReconnectRef.current = false;
    reconnectAttemptsRef.current = 0; // 重连成功后重置计数
  };

  const handleAutoReconnect = () => {
    if (isManualStopRef.current || !conversationIdRef.current) {
      console.log('[VoiceEngine] Auto-reconnect skipped: Manual stop or no conversation active');
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error('[VoiceEngine] Max reconnect attempts reached');
      if (onErrorCallback) onErrorCallback('Connection lost and could not be recovered automatically.');
      return;
    }

    const delay = backoffDelays[reconnectAttemptsRef.current];
    reconnectAttemptsRef.current++;

    console.log(`[VoiceEngine] Attempting auto-reconnect ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${delay}ms...`);
    
    if (onConnectionLostCallback) {
      onConnectionLostCallback(reconnectAttemptsRef.current, delay);
    }

    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(async () => {
      try {
        console.log('[VoiceEngine] Executing auto-reconnect start...');
        // 强制使用 resume 意图，并携带最后的 conversationId
        await start({
          intent: 'resume',
          conversationId: conversationIdRef.current || undefined,
          speakerId: speakerRef.current || undefined,
          systemRole: systemRoleRef.current || undefined,
          personaId: personaIdRef.current || undefined,
          gender: genderRef.current || undefined,
          userName: userNameRef.current || undefined,
          textOnly: textOnlyModeRef.current,
        });
        console.log('[VoiceEngine] Auto-reconnect successful');
      } catch (err) {
        console.error('[VoiceEngine] Auto-reconnect attempt failed:', err);
        // 如果 start 内部抛错，socket 应该会被关闭，进而再次触发 onclose 进入下一次 handleAutoReconnect
      }
    }, delay);
  };

  async function connect(options?: VoiceEngineOptions): Promise<boolean> {
    // 更新 provider 和 language
    if (options?.provider) {
      providerRef.current = options.provider;
    }
    if (options?.language) {
      languageRef.current = options.language;
    }
    // Ensure AudioManager is initialized (it might have been destroyed in stop())
    if (!audioManagerRef.current) {
      // Reset iOS AVAudioSession before creating new AudioContext
      // audioContext.close() deactivates AVAudioSession, losing .defaultToSpeaker → earpiece routing
      try {
        await AudioSession.resetAudioSession();
        console.log('[VoiceEngine] AVAudioSession reset before new AudioManager');
      } catch (e) {
        console.warn('[VoiceEngine] AudioSession reset failed (expected on web):', e);
      }

      console.log('[VoiceEngine] Creating new AudioManager instance');
      audioManagerRef.current = new AudioManager();

      audioManagerRef.current.setAudioCallback((audioData: ArrayBuffer) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(audioData);
        }
      });

      // Track TTS Playback status to notify UI when AI is talking
      audioManagerRef.current.setOnPlaybackStateChange((isSpeaking) => {
        if (onAISpeakingStateChangeCallback) {
          onAISpeakingStateChangeCallback(isSpeaking);
        }

        // --- 核心修复：收到了 chat_ended，且音频播完了，才执行保存 ---
        if (!isSpeaking && isWaitingForPlaybackToEndRef.current) {
          console.log('[VoiceEngine] Playback finished after chat_ended, pushing messages now.');
          isWaitingForPlaybackToEndRef.current = false;
          pushToMessages();
          resetInactivityTimer();
        }
      });

      // Set logger to forward logs to backend
      audioManagerRef.current.setLogger((msg, level) => {
        log(msg, level);
      });
    } else {
      // 复用 pre-created AudioManager：iOS 后台会 interrupt AVAudioSession，
      // 导致已有 AudioContext 的音频路由丢失（走听筒）。
      // 必须重置音频会话并刷新 AudioContext 路由。
      try {
        await AudioSession.resetAudioSession();
        await audioManagerRef.current.refreshAudioRouting();
        console.log('[VoiceEngine] Audio routing refreshed for existing AudioManager');
      } catch (e) {
        console.warn('[VoiceEngine] Audio routing refresh failed (expected on web):', e);
      }
    }

    // 如果 WebSocket 已经打开，直接返回
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      if (needsReconnectRef.current) {
        sendConnectPayload();
        return true; // Re-sent payload effectively acts like a new session setup
      }
      return false; // Existing connection, no new setup performed
    }

    // 如果正在连接中，等待连接完成
    if (connectPromiseRef.current) {
      await connectPromiseRef.current;
      return false; // Joined existing connection attempt
    }

    // 如果 WebSocket 存在但处于 CONNECTING 状态，等待它打开
    if (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING) {
      connectPromiseRef.current = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000); // 10秒超时

        // 保存原有的处理器
        const originalOnopen = socketRef.current!.onopen;
        const originalOnerror = socketRef.current!.onerror;

        socketRef.current!.onopen = (event) => {
          clearTimeout(timeout);
          // 调用原有的处理器
          if (originalOnopen) {
            originalOnopen.call(socketRef.current!, event);
          }
          if (needsReconnectRef.current) {
            sendConnectPayload();
          }
          connectPromiseRef.current = null;
          resolve();
        };

        socketRef.current!.onerror = (event) => {
          clearTimeout(timeout);
          // 调用原有的处理器
          if (originalOnerror) {
            originalOnerror.call(socketRef.current!, event);
          }
          connectPromiseRef.current = null;
          reject(event);
        };
      });
      await connectPromiseRef.current;
      return true; // Successfully waited for connecting socket
    }

    // 创建新的 WebSocket 连接
    if (!socketRef.current) {
      // Check and refresh token if needed (before creating the socket promise)
      let token = getJWTToken();
      if (token) {
        const isValid = await verifyJWTToken(token);
        if (!isValid) {
          console.log('[VoiceEngine] Token invalid or expired, attempting refresh...');
          try {
            const newTokenData = await refreshJWTToken();
            token = newTokenData.jwt_token;
            console.log('[VoiceEngine] Token refreshed successfully before connection');
          } catch (refreshErr) {
            console.warn('[VoiceEngine] Failed to refresh token:', refreshErr);
            // Token might be cleared by refreshJWTToken if it failed
            token = getJWTToken();
          }
        }
      }

      connectPromiseRef.current = new Promise<void>((resolve, reject) => {
        // 获取 JWT Token 并添加到 WebSocket URL
        const wsUrl = token ? `${SERVER_URL}?token=${encodeURIComponent(token)}` : SERVER_URL;

        if (!token) {
          console.warn('[VoiceEngine] No JWT token found. Connection may fail if auth is enabled on server.');
        } else {
          console.log('[VoiceEngine] Connecting with JWT authentication');
        }

        socketRef.current = new WebSocket(wsUrl);
        socketRef.current.binaryType = 'arraybuffer'; // Optimize for binary audio data

        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000); // 10秒超时

        socketRef.current.onopen = () => {
          clearTimeout(timeout);
          sendConnectPayload();
          console.log(`[VoiceEngine] Connected with JWT auth, provider: ${providerRef.current}, language: ${languageRef.current}`);
          connectPromiseRef.current = null;
          resolve();
        };

        socketRef.current.onmessage = async (event) => {
          const allowTtsPlayback =
            !textOnlyModeRef.current && shouldPlayRef.current;

          if (event.data instanceof ArrayBuffer) {
            // Direct ArrayBuffer (preferred)
            const arrayBuffer = event.data;
            if (allowTtsPlayback) {
              audioManagerRef.current?.playAudio(arrayBuffer);
            } else if (!textOnlyModeRef.current) {
              console.log(`[VoiceEngine] Skipping audio chunk (shouldPlay=false): ${arrayBuffer.byteLength} bytes`);
            }
          } else if (event.data instanceof Blob) {
            // Fallback for Blob (if binaryType not supported/ignored)
            const arrayBuffer = await event.data.arrayBuffer();
            if (allowTtsPlayback) {
              audioManagerRef.current?.playAudio(arrayBuffer);
            } else if (!textOnlyModeRef.current) {
              console.log(`[VoiceEngine] Skipping audio chunk (shouldPlay=false): ${arrayBuffer.byteLength} bytes`);
            }
          } else {
            const msg = JSON.parse(event.data);
            console.log(`[VoiceEngine] Message received: ${msg.event || 'unnamed_event'}`);
            if (msg.user_text) {
              devLog.sensitive(`[VoiceEngine] User text: ${msg.user_text}`);
              userTextRef.current = msg.user_text
              flushSync(() => onUserTextUpdate(msg.user_text));
            }

            if (msg.ai_reply) {
              devLog.sensitive(`[VoiceEngine] AI reply: ${msg.ai_reply}`);
              aiTextRef.current += msg.ai_reply;
              // 实时更新 AI 文本到 UI
              if (onAiTextUpdate) {
                flushSync(() => onAiTextUpdate(aiTextRef.current));
              }
            }

            if (msg.event === "user_speaking") {
              console.log(`[VoiceEngine] Event: user_speaking (stopping playback, shouldPlay=false)`);
              userSpeakingTimestampRef.current = Date.now(); // 记录时间戳，用于延迟过滤
              shouldPlayRef.current = false
              audioManagerRef.current?.stopPlayback();
              clearInactivityTimer();
              clearNoResponseTimeout();

              // 清除延迟保存的定时器（如果有）
              if (pushMessagesTimerRef.current) {
                clearTimeout(pushMessagesTimerRef.current);
                pushMessagesTimerRef.current = null;
              }

              // 立即保存消息（用户打断时）
              pushToMessages();
              if (onSpeechStartCallback) onSpeechStartCallback();
            } else if (msg.event === "asr_ended") {
              // 延迟过滤机制：检测是否为AI声音的误识别
              const speakingDuration = userSpeakingTimestampRef.current > 0
                ? Date.now() - userSpeakingTimestampRef.current
                : 0;

              const userTextLength = userTextRef.current.length;

              // 过滤条件：
              // 1. 持续时间 < 200ms：几乎肯定是误触发/短噪音
              // 2. 持续时间 < 1000ms 且 没有识别出任何文本：纯噪音/静音
              const isLikelyEcho = speakingDuration > 0 &&
                (speakingDuration < 200 || (speakingDuration < 1000 && userTextLength === 0));

              if (isLikelyEcho) {
                console.log(`[VoiceEngine] ⚠️ 检测到疑似AI回声误触发 - 持续时间: ${speakingDuration}ms, 文本长度: ${userTextLength}, 内容: "${userTextRef.current}"`);
                console.log(`[VoiceEngine] 已忽略此次识别，清空误识别文本`);

                // 清空误识别的文本
                userTextRef.current = "";
                flushSync(() => onUserTextUpdate(""));
              } else {
                console.log(`[VoiceEngine] Event: asr_ended - 正常识别 (持续时间: ${speakingDuration}ms, 文本长度: ${userTextLength})`);
              }

              shouldPlayRef.current = true;
              userSpeakingTimestampRef.current = 0; // 重置时间戳
              if (onSpeechEndCallback) onSpeechEndCallback();
            } else if (msg.event === "chat_ended") {
              const playing = audioManagerRef.current?.isPlaying;
              const totalDurationMs = msg.total_duration_ms || 0;
              
              console.log(`[VoiceEngine] Event: chat_ended (Currently playing: ${playing}, totalDuration: ${totalDurationMs}ms)`);

              if (playing) {
                // 如果还在放，先标记，等 setOnPlaybackStateChange 触发时再存
                isWaitingForPlaybackToEndRef.current = true;
                
                // --- 核心优化：动态计算安全兜底时间 ---
                // 取总时长 + 2秒，且不少于 30s（防止之前的固定 5s 过短中断长录音）
                const safetyTimeout = Math.max(30000, totalDurationMs + 2000);
                
                if (pushMessagesTimerRef.current) clearTimeout(pushMessagesTimerRef.current);
                pushMessagesTimerRef.current = window.setTimeout(() => {
                  if (isWaitingForPlaybackToEndRef.current) {
                    console.log(`[VoiceEngine] Safety timeout triggered for chat_ended after ${safetyTimeout}ms`);
                    isWaitingForPlaybackToEndRef.current = false;
                    pushToMessages();
                    resetInactivityTimer();
                  }
                }, safetyTimeout);
              } else {
                // 如果没在放（或者是纯文本流），直接延迟一下存
                if (pushMessagesTimerRef.current) clearTimeout(pushMessagesTimerRef.current);
                pushMessagesTimerRef.current = window.setTimeout(() => {
                  console.log(`[VoiceEngine] Saving messages (delayed after chat_ended - no audio)`);
                  pushToMessages();
                  resetInactivityTimer();
                  pushMessagesTimerRef.current = null;
                }, 500);
              }
            } else if (msg.event === 'session_ready') {
              console.log('[VoiceEngine] Session ready received from backend');
              // Greeting finished, save it to messages so it doesn't linger in buffer
              pushToMessages();
               if (msg.conversation_id) {
                conversationIdRef.current = msg.conversation_id;
              }
              console.log('[VoiceEngine] Triggering onSessionReadyCallback...');
              if (onSessionReadyCallback) onSessionReadyCallback(msg.conversation_id);
            } else if (msg.event === 'conversation_assigned') {
              console.log('[VoiceEngine] Conversation assigned:', msg.conversation_id);
              conversationIdRef.current = msg.conversation_id;
              console.log('[VoiceEngine] Triggering onSessionReadyCallback (assigned)...');
              if (onSessionReadyCallback) onSessionReadyCallback(msg.conversation_id);
            } else if (msg.event === 'error') {
              console.error('[VoiceEngine] Error received from backend:', msg.message);
              if (onErrorCallback) onErrorCallback(msg.message);
            } else if (msg.event === 'quota_exceeded') {
              console.warn('[VoiceEngine] Quota exceeded:', msg.message);
              if (onQuotaExceededCallback) onQuotaExceededCallback(msg.message);
              // Stop the session gracefully
              stop();
            } else if (msg.event === 'rate_limit') {
              console.warn(`[VoiceEngine] Rate limit exceeded. Retry after ${msg.retry_after}s`);
              if (onRateLimitCallback) {
                onRateLimitCallback(msg.retry_after || 1);
              }
              // Optional: Stop session or let UI handle it?
              // Usually we want to stop "connecting" state in UI.
              // stop() might be too aggressive if we just want to retry?
              // But 'connect' command failed, so session is borked.
              stop();
            } else if (msg.event === 'suggestions') {
              console.log('[VoiceEngine] Suggestions received:', msg.content);
              if (onSuggestionsCallback) {
                onSuggestionsCallback(msg.content || []);
              }
            } else if (msg.event === 'summary_debug') {
              console.log('[VoiceEngine] Summary debug:', msg);
              const convId = msg?.conversation_id;
              const result = msg?.result;
              if (convId && result) {
                summaryDebugByConversationRef.current[convId] = result;
                if (isTerminalSummaryResult(result)) {
                  const waiters = summaryResultWaitersRef.current[convId] || [];
                  waiters.forEach((resolve) => resolve(result));
                  delete summaryResultWaitersRef.current[convId];
                }
              }
            } else if (msg.event === 'word_boundary') {
              console.log(`[VoiceEngine] WordBoundary: "${msg.text}" offset=${msg.offset}ms`);
              if (onWordBoundaryCallback) {
                onWordBoundaryCallback({
                  text: msg.text,
                  offset: msg.offset
                });
              }
            }
          }
        };

        socketRef.current.onclose = () => {
          console.log('WebSocket closed');
          socketRef.current = null;
          connectPromiseRef.current = null;
          shouldStartTimerRef.current = false;
          clearInactivityTimer();
          clearNoResponseTimeout();

          // 触发自动重连
          if (!isManualStopRef.current) {
            handleAutoReconnect();
          }
        };

        socketRef.current.onerror = (e) => {
          clearTimeout(timeout);
          console.error('WebSocket error:', e);
          connectPromiseRef.current = null;
          reject(e);
        };
      });

      await connectPromiseRef.current;
      return true; // New connection established
    }
    return false; // Type safety fallback, though !socketRef.current covered above
  }

  function setupVisibilityHandler() {
    // 防止重复注册
    if (visibilityHandlerRef.current) return;

    const handler = () => {
      if (document.hidden) {
        // --- App 进后台 ---
        // 只有在对话活跃时（有 audioManager 且非用户主动停止）才 handle
        if (!audioManagerRef.current || isManualStopRef.current) return;

        console.log('[VoiceEngine] App went to background, releasing mic hardware');
        wasActiveBeforeHiddenRef.current = true;
        // 释放麦克风硬件（track.stop()）让 iOS 橙色指示灯消失
        // 回前台时会重建 AudioManager，所以这里可以完全释放
        audioManagerRef.current.releaseHardware().catch(() => {});
        // 不主动断 WebSocket，让系统自然超时
      } else {
        // --- App 回前台 ---
        if (!wasActiveBeforeHiddenRef.current) return;
        wasActiveBeforeHiddenRef.current = false;

        console.log('[VoiceEngine] App returned to foreground');

        if (socketRef.current?.readyState === WebSocket.OPEN) {
          // WebSocket 还活着 → 销毁旧 AudioManager 并创建新的
          // iOS 后台 interrupt AVAudioSession 后，已运行的 AudioContext 无法恢复音频路由，
          // suspend/resume 也无效，唯一可靠方案是重建 AudioManager（新 AudioContext）
          console.log('[VoiceEngine] WebSocket alive, recreating AudioManager for routing fix');
          (async () => {
            try {
              // 销毁旧的（释放麦克风硬件 + 关闭 AudioContext）
              if (audioManagerRef.current) {
                await audioManagerRef.current.releaseHardware();
                audioManagerRef.current = null;
              }
              // 重置音频会话 + 创建新 AudioManager
              await AudioSession.resetAudioSession();
              const am = new AudioManager();
              am.setAudioCallback((audioData: ArrayBuffer) => {
                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                  socketRef.current.send(audioData);
                }
              });
              am.setOnPlaybackStateChange((isSpeaking) => {
                if (onAISpeakingStateChangeCallback) {
                  onAISpeakingStateChangeCallback(isSpeaking);
                }
                if (!isSpeaking && isWaitingForPlaybackToEndRef.current) {
                  console.log('[VoiceEngine] Playback finished after chat_ended, pushing messages now.');
                  isWaitingForPlaybackToEndRef.current = false;
                  pushToMessages();
                  resetInactivityTimer();
                }
              });
              am.setLogger((msg, level) => {
                log(msg, level);
              });
              audioManagerRef.current = am;
              if (!textOnlyModeRef.current) {
                await audioManagerRef.current.startMicCapture();
                console.log('[VoiceEngine] AudioManager recreated, mic resumed after background');
              } else {
                console.log('[VoiceEngine] Text-only session: skipping mic after background resume');
              }
              if (onVisibilityResumeCallback) onVisibilityResumeCallback();
            } catch (err) {
              console.warn('[VoiceEngine] Visibility resume error:', err);
            }
          })();
        } else if (!isManualStopRef.current) {
          // WebSocket 已断且不是用户主动停的
          // handleAutoReconnect 会在 onclose 回调中自动触发，这里不重复调用
          console.log('[VoiceEngine] WebSocket dead after background, auto-reconnect will handle recovery');
        }
      }
    };

    document.addEventListener('visibilitychange', handler);
    visibilityHandlerRef.current = handler;
  }

  function teardownVisibilityHandler() {
    if (visibilityHandlerRef.current) {
      document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      visibilityHandlerRef.current = null;
    }
    wasActiveBeforeHiddenRef.current = false;
  }

  /* New Ref for start lock */
  const startPromiseRef = { current: null as Promise<void> | null };

  async function start(options?: StartSessionOptions) {
    if (startPromiseRef.current) {
      console.log('[VoiceEngine] Start already in progress, waiting...');
      await startPromiseRef.current;
      return;
    }

    startPromiseRef.current = (async () => {
      // Prevent race condition: wait for any pending stop to complete
      if (stopPromiseRef.current) {
        console.log('[VoiceEngine] Waiting for pending stop to complete...');
        await stopPromiseRef.current;
      }
      
      // 取消待执行的 AudioManager 延迟销毁（用户在 3s 内开始了新会话）
      if (audioManagerCleanupTimerRef.current) {
        clearTimeout(audioManagerCleanupTimerRef.current);
        audioManagerCleanupTimerRef.current = null;
        console.log('[VoiceEngine] Cancelled pending AudioManager cleanup');
      }

      // 只要调用了 start，就认为不是人为停止（除非后续显式调用 stop）
      isManualStopRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (options?.speakerId) {
        speakerRef.current = options.speakerId;
      }
      if (options?.systemRole) {
        systemRoleRef.current = options.systemRole;
      }
      if (options?.intent) {
        intentRef.current = options.intent;
      } else {
        intentRef.current = 'new'; // Default fallback
      }
      if (options?.userName) {
        userNameRef.current = options.userName;
      }
      if (options?.gender) {
        genderRef.current = options.gender;
      }
      if (options?.personaId) {
        personaIdRef.current = options.personaId;
      }
      if (options?.conversationId) {
        conversationIdRef.current = options.conversationId;
      }
      if (typeof options?.textOnly === 'boolean') {
        textOnlyModeRef.current = options.textOnly;
      }
      endSessionSentRef.current = false;

      const isNewConnection = await connect();

      // 确保 WebSocket 已打开
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket is not open');
      }

      // 只有在复用旧连接时才手动发送 connect 负载
      // 如果是新连接，onopen 回调已经发送过了，避免重复
      if (!isNewConnection) {
        console.log('[VoiceEngine] Reusing existing connection, sending connect payload');
        sendConnectPayload();
      } else {
        console.log('[VoiceEngine] New connection established, payload already sent via onopen');
      }

      // 清除之前可能存在的延迟保存定时器
      if (pushMessagesTimerRef.current) {
        clearTimeout(pushMessagesTimerRef.current);
        pushMessagesTimerRef.current = null;
      }

      // 重置 AudioManager 的播放状态（清理上次会话的残留状态）
      audioManagerRef.current?.resetPlaybackState();

      // 打字模式不朗读：丢弃服务端 TTS 音频，仅依赖 JSON 里的 ai_reply 文本
      shouldPlayRef.current = !textOnlyModeRef.current;
      console.log(
        `[VoiceEngine] shouldPlay initialized to ${shouldPlayRef.current} (textOnly=${textOnlyModeRef.current})`,
      );

      if (textOnlyModeRef.current) {
        console.log('[VoiceEngine] Text-only session: skipping microphone capture');
      } else {
        try {
          console.log('[VoiceEngine] Requesting microphone access...');
          await audioManagerRef.current?.startMicCapture();
          console.log('[VoiceEngine] Microphone access granted');
        } catch (error) {
          console.error('[VoiceEngine] Failed to start microphone:', error);
          if (onErrorCallback) onErrorCallback("Microphone access denied or failed");
          throw error;
        }
      }

      const startMsg = { command: "start" };
      socketRef.current.send(JSON.stringify(startMsg));
      console.log(`[VoiceEngine] Session started`);

      setupVisibilityHandler();

      shouldStartTimerRef.current = providerRef.current === 'huoshan';
      clearNoResponseTimeout();
      if (shouldStartTimerRef.current) {
        resetInactivityTimer();
      } else {
        clearInactivityTimer();
      }
    })();

    try {
      await startPromiseRef.current;
    } finally {
      startPromiseRef.current = null;
    }
  }

  async function stop() {
    if (stopPromiseRef.current) {
      return stopPromiseRef.current;
    }

    stopPromiseRef.current = (async () => {
      isManualStopRef.current = true; // 标记为人为停止，防止触发自动重连
      textOnlyModeRef.current = false;
      teardownVisibilityHandler();

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      shouldStartTimerRef.current = false;
      clearInactivityTimer();
      clearNoResponseTimeout();

      // 停止前保存当前缓冲区中的 messages（防止用户手动停止时丢失对话）
      await pushToMessages();

      // 停止 AudioManager 的录音和播放
      if (audioManagerRef.current) {
        await audioManagerRef.current.stopSession();

        // 延迟 3s 后彻底销毁 AudioManager（释放麦克风硬件、关闭 AudioContext）
        // 如果 3s 内 start() 被调用，timer 会被取消，AudioManager 复用（走 unmute 路径）
        // 为什么不在 AudioManager 内部做部分释放：iOS WebKit 的 ScriptProcessorNode
        // 在同一 AudioContext 上 track.stop() 后无法恢复，必须创建全新的 AudioContext
        if (audioManagerCleanupTimerRef.current) {
          clearTimeout(audioManagerCleanupTimerRef.current);
        }
        audioManagerCleanupTimerRef.current = setTimeout(async () => {
          audioManagerCleanupTimerRef.current = null;
          if (!audioManagerRef.current) return;

          console.log('[VoiceEngine] Delayed AudioManager cleanup: releasing all hardware');
          await audioManagerRef.current.releaseHardware();
          audioManagerRef.current = null;

          // 预创建新 AudioManager：将 resetAudioSession + new AudioContext 的开销
          // 提前消化，下次 connect() 时可直接复用，减少用户感知延迟
          try {
            await AudioSession.resetAudioSession();
            console.log('[VoiceEngine] Pre-creating AudioManager after cleanup');
            const am = new AudioManager();
            am.setAudioCallback((audioData: ArrayBuffer) => {
              if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.send(audioData);
              }
            });
            am.setOnPlaybackStateChange((isSpeaking) => {
              if (onAISpeakingStateChangeCallback) {
                onAISpeakingStateChangeCallback(isSpeaking);
              }
              if (!isSpeaking && isWaitingForPlaybackToEndRef.current) {
                console.log('[VoiceEngine] Playback finished after chat_ended, pushing messages now.');
                isWaitingForPlaybackToEndRef.current = false;
                pushToMessages();
                resetInactivityTimer();
              }
            });
            am.setLogger((msg, level) => {
              log(msg, level);
            });
            // 只有在预创建期间没有被其他操作抢占时才赋值
            if (!audioManagerRef.current) {
              audioManagerRef.current = am;
            } else {
              // 有人已经创建了（比如快速 start），丢弃预创建的
              am.cleanup();
            }
          } catch (e) {
            console.warn('[VoiceEngine] Pre-create AudioManager failed (will lazy-create in connect):', e);
          }
        }, 3000);
      }

      // 确保 WebSocket 已打开再发送
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN &&
        !endSessionSentRef.current
      ) {
        const endMsg = { command: "end_session" };
        socketRef.current.send(JSON.stringify(endMsg));
        endSessionSentRef.current = true;
        console.log('[VoiceEngine] Summary debug: sent end_session command');
      } else if (endSessionSentRef.current) {
        console.log('[VoiceEngine] Summary debug: end_session already sent for this session');
      } else {
        console.warn('[VoiceEngine] Summary debug: end_session not sent (socket not open)');
      }

      needsReconnectRef.current = true;
      console.log(`[VoiceEngine] Session ended`);
    })();

    try {
      await stopPromiseRef.current;
    } finally {
      stopPromiseRef.current = null;
    }
  }

  async function cleanup() {
    // 注销 visibility 监听
    teardownVisibilityHandler();

    // 取消延迟销毁 AudioManager 的 timer（cleanup 会直接销毁）
    if (audioManagerCleanupTimerRef.current) {
      clearTimeout(audioManagerCleanupTimerRef.current);
      audioManagerCleanupTimerRef.current = null;
    }

    // 清除延迟保存 messages 的定时器
    if (pushMessagesTimerRef.current) {
      clearTimeout(pushMessagesTimerRef.current);
      pushMessagesTimerRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    clearInactivityTimer();
    clearNoResponseTimeout();
    shouldStartTimerRef.current = false;

    // 彻底清理 AudioManager（包括停止播放、停止麦克风、关闭 AudioContext）
    if (audioManagerRef.current) {
      await audioManagerRef.current.cleanup();
      audioManagerRef.current = null;
    }

    // 关闭 WebSocket 连接
    if (socketRef.current) {
      if (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING) {
        socketRef.current.close();
      }
      socketRef.current = null;
    }

    // 重置状态
    needsReconnectRef.current = false;
    connectPromiseRef.current = null;
    userTextRef.current = "";
    aiTextRef.current = "";
    shouldPlayRef.current = true;  // 重置为 true，下次会话可以正常播放
    speakerRef.current = null;
    systemRoleRef.current = null;
    conversationIdRef.current = null;
    endSessionSentRef.current = false;

    console.log('[VoiceEngine] 资源已彻底清理');
  }

  async function pushToMessages() {
    const hasUserText = Boolean(userTextRef.current);
    const hasAiText = Boolean(aiTextRef.current);

    if (!hasUserText && !hasAiText) {
      console.log('[VoiceEngine] pushToMessages: No buffered content, skipping');
      return;
    }

    // 处理“系统提示”类 messages：没有用户输入但有 AI 回复
    if (!hasUserText && hasAiText) {
      devLog.sensitive(`[VoiceEngine] pushToMessages: System AI="${aiTextRef.current.substring(0, 30)}..."`);

      if (setChatMessages) {
        setChatMessages(prev => [
          ...prev,
          { role: 'assistant', content: aiTextRef.current },
        ]);
      } else if (setUIMessages) {
        flushSync(() => setUIMessages(prev => [
          ...prev,
          { id: crypto.randomUUID(), text: aiTextRef.current, ts: Date.now(), role: 'assistant' },
        ]));
      }

      aiTextRef.current = '';
      if (onAiTextUpdate) {
        onAiTextUpdate('');
      }
      return;
    }

    devLog.sensitive(`[VoiceEngine] pushToMessages: User="${userTextRef.current.substring(0, 30)}...", AI="${aiTextRef.current.substring(0, 30)}..." (total ${aiTextRef.current.length} chars)`);

    // Capture current values before clearing
    const userText = userTextRef.current;
    const aiText = aiTextRef.current;

    // 1. Clear "live" display first to prevent duplicates during state update re-render
    if (onClearLastText) {
      onClearLastText();
    }
    if (onAiTextUpdate) {
      onAiTextUpdate('');
    }

    // 2. Clear internal buffers
    aiTextRef.current = '';
    userTextRef.current = '';

    // 3. Update persistent history state
    if (setChatMessages) {
      setChatMessages(prev => {
        const newMessages: ChatMsg[] = [...prev, { role: 'user', content: userText }];
        if (aiText.trim()) {
          newMessages.push({ role: 'assistant', content: aiText });
        }
        return newMessages;
      });
    } else if (setUIMessages) {
      flushSync(() => setUIMessages(prev => {
        const newMessages: UIMsg[] = [...prev, { id: crypto.randomUUID(), text: userText, ts: Date.now(), role: 'user' }];
        if (aiText.trim()) {
          newMessages.push({ id: crypto.randomUUID(), text: aiText, ts: Date.now(), role: 'assistant' });
        }
        return newMessages;
      }));
    }

    console.log('[VoiceEngine] pushToMessages: Messages saved and cleared');
  }

  function setProvider(provider: VoiceProvider) {
    providerRef.current = provider;
    console.log(`[VoiceEngine] Provider set to: ${provider}`);
  }

  function setLanguage(language: string) {
    languageRef.current = language;
    console.log(`[VoiceEngine] Language set to: ${language}`);
  }

  function setSpeakerId(speakerId: string | null) {
    speakerRef.current = speakerId;
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command: 'update_config',
        speaker: speakerId
      }));
    }
  }

  function setSystemRole(systemRole: string | null) {
    systemRoleRef.current = systemRole;
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command: 'update_config',
        system_role: systemRole
      }));
    }
  }

  function setPersonaId(personaId: string | null) {
    personaIdRef.current = personaId;
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command: 'update_config',
        persona_id: personaId
      }));
    }
  }

  function setGender(gender: string | null) {
    genderRef.current = gender;
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command: 'update_config',
        gender: gender
      }));
    }
  }

  function setUserName(userName: string | null) {
    userNameRef.current = userName;
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command: 'update_config',
        user_name: userName
      }));
    }
  }

  function log(message: string, level: 'info' | 'debug' | 'warning' | 'error' = 'info') {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command: 'log',
        message,
        level
      }));
    }
  }

  function triggerCommand(command: string, payload?: Record<string, any>) {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        command,
        ...payload
      }));
    }
  }

  function waitForSummaryResult(conversationId: string, timeoutMs: number = 6000): Promise<any | null> {
    if (!conversationId) return Promise.resolve(null);

    const existing = summaryDebugByConversationRef.current[conversationId];
    if (existing && isTerminalSummaryResult(existing)) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      if (!summaryResultWaitersRef.current[conversationId]) {
        summaryResultWaitersRef.current[conversationId] = [];
      }

      const timeoutId = setTimeout(() => {
        const waiters = summaryResultWaitersRef.current[conversationId] || [];
        summaryResultWaitersRef.current[conversationId] = waiters.filter((fn) => fn !== wrappedResolve);
        resolve(null);
      }, timeoutMs);

      const wrappedResolve = (result: any | null) => {
        clearTimeout(timeoutId);
        resolve(result);
      };

      summaryResultWaitersRef.current[conversationId].push(wrappedResolve);
    });
  }

  return {
    connect,
    start,
    stop,
    cleanup,
    setProvider,
    setLanguage,
    setSpeakerId,
    setSystemRole,
    setPersonaId,
    setGender,
    setUserName,
    log,
    onSessionReady: (callback: (id?: string) => void) => {
      onSessionReadyCallback = callback;
    },
    onError: (callback: (message: string) => void) => {
      onErrorCallback = callback;
    },
    onQuotaExceeded: (callback: (message: string) => void) => {
      onQuotaExceededCallback = callback;
    },
    onRateLimit: (callback: (retryAfter: number) => void) => {
      onRateLimitCallback = callback;
    },
    onSpeechStart: (callback: () => void) => {
      onSpeechStartCallback = callback;
    },
    onSpeechEnd: (callback: () => void) => {
      onSpeechEndCallback = callback;
    },
    onAISpeakingStateChange: (callback: (isSpeaking: boolean) => void) => {
      onAISpeakingStateChangeCallback = callback;
    },
    onSuggestions: (callback: (suggestions: string[]) => void) => {
      onSuggestionsCallback = callback;
    },
    onWordBoundary: (callback: (data: WordBoundary) => void) => {
      onWordBoundaryCallback = callback;
    },
    onConnectionLost: (callback: (retryCount: number, waitTime: number) => void) => {
      onConnectionLostCallback = callback;
    },
    onVisibilityResume: (callback: () => void) => {
      onVisibilityResumeCallback = callback;
    },
    resetPlaybackTimer: () => {
      audioManagerRef.current?.resetPlaybackTimer();
    },
    get audioManager() {
      return audioManagerRef.current;
    },
    triggerCommand,
    waitForSummaryResult,
    getBufferedContent: () => {
      return {
        userText: userTextRef.current,
        aiText: aiTextRef.current
      };
    }
  };
}
